import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import { cn } from '@/utils/cn';
import type { TournamentDeletionCurrencyAmount, TournamentDeletionPreviewView } from '@/models/tournament';
import { MatchStatus, resolveEnum } from '@/models/tournament';
import ClawbackSummaryList from '../../shared/ClawbackSummaryList';
import { formatCurrencyAmount } from '../../shared/clawbackFormat';

/**
 * CO-922 (owner decisions 2026-10-21): the measured preview of an HR cascade delete.
 *
 * Label/value rows, never plural sentences: Arabic has six plural forms and the i18n audit cannot see a
 * missing one, so "Matches / 4" is the only shape that reads right in en, ar and fr. At most five rows per
 * group on screen; everything finer sits in the collapsed Details disclosure (CLAUDE.md data-dump rule).
 */

const MATCH_STATUS_KEYS: Record<number, string> = {
  [MatchStatus.Scheduled]: 'Scheduled',
  [MatchStatus.InProgress]: 'In Progress',
  [MatchStatus.Completed]: 'Completed',
  [MatchStatus.Closed]: 'Closed',
  [MatchStatus.UnderRevision]: 'Under Revision',
  [MatchStatus.Cancelled]: 'Cancelled',
};

function formatPreviewAmount(row: TournamentDeletionCurrencyAmount, language: string): string {
  return formatCurrencyAmount(row.amount, row.currencyCode, language);
}

const Row: React.FC<{ label: string; value: React.ReactNode; testId: string; muted?: boolean }> = ({ label, value, testId, muted }) => (
  <div className="flex items-baseline justify-between gap-4 py-1.5" data-testid={testId}>
    <dt className={cn('text-sm min-w-0', muted ? 'text-muted-foreground' : 'text-foreground')}>{label}</dt>
    <dd className="text-sm font-semibold tabular-nums text-foreground shrink-0" data-testid={`${testId}-value`}>{value}</dd>
  </div>
);

interface DeleteTournamentPreviewProps {
  preview: TournamentDeletionPreviewView;
}

const DeleteTournamentPreview: React.FC<DeleteTournamentPreviewProps> = ({ preview }) => {
  const { t, i18n } = useTranslation('tournament');
  const lang = i18n.language || 'en';
  // Round 2: who loses which amount. The typed name against this preview IS HR's consent; the server ties
  // the confirmation to these exact lines (fingerprint per company, actor and line).
  const clawbackOfficials = preview.clawbackOfficials ?? [];
  const hasMoney = preview.assignmentsOnUnconsumedPay > 0 || preview.assignmentsOnConsumedPay > 0 || clawbackOfficials.length > 0;
  const assignments = preview.liveAssignmentCount + preview.archivedAssignmentCount;

  return (
    <div className="space-y-5" data-testid="delete-tournament-preview">
      {/* Will be removed */}
      <section aria-labelledby="cascade-delete-removed-heading">
        <h3 id="cascade-delete-removed-heading" className="text-xs font-semibold uppercase tracking-wide text-red-700 dark:text-red-300 mb-1">
          {t('cascadeDelete.removedHeading')}
        </h3>
        <dl className="divide-y divide-border">
          <Row label={t('cascadeDelete.row.matches')} value={preview.matchCount} testId="delete-preview-matches" />
          <Row label={t('cascadeDelete.row.assignments')} value={assignments} testId="delete-preview-assignments" />
          <Row label={t('cascadeDelete.row.scheduledShifts')} value={preview.scheduledShiftsToDelete} testId="delete-preview-shifts" />
          <Row label={t('cascadeDelete.row.ratings')} value={preview.ratingCount} testId="delete-preview-ratings" />
          <Row label={t('cascadeDelete.row.scheduleBlocks')} value={preview.scheduleBlockCount} testId="delete-preview-schedule-blocks" />
        </dl>
      </section>

      {/* Kept */}
      <section aria-labelledby="cascade-delete-kept-heading">
        <h3 id="cascade-delete-kept-heading" className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300 mb-1">
          {t('cascadeDelete.keptHeading')}
        </h3>
        <dl className="divide-y divide-border">
          <Row label={t('cascadeDelete.row.clockIns')} value={preview.clockInsKept} testId="delete-preview-clockins" />
          <Row label={t('cascadeDelete.row.retainedShifts')} value={preview.scheduledShiftsRetained} testId="delete-preview-retained-shifts" />
          <Row label={t('cascadeDelete.row.hourlyPayItems')} value={preview.hourlyPayItemsKept} testId="delete-preview-hourly-pay" />
        </dl>
        <p className="mt-1 text-xs text-muted-foreground">{t('cascadeDelete.keptPayslipsNote')}</p>
      </section>

      {/* Money: only when something already sits on a pay line (CO-810 cases b and c). */}
      {hasMoney && (
        <section
          aria-labelledby="cascade-delete-pay-heading"
          className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/30"
          data-testid="delete-tournament-money"
        >
          <h3 id="cascade-delete-pay-heading" className="flex items-center gap-2 text-sm font-semibold text-amber-900 dark:text-amber-200">
            <Icon name="Banknote" size={16} className="shrink-0" />
            {t('cascadeDelete.payHeading')}
          </h3>
          <dl className="mt-2 divide-y divide-amber-200/70 dark:divide-amber-800/70">
            {preview.unconsumedReductionByCurrency.map((row) => (
              <Row
                key={`u-${row.currencyId}`}
                label={t('cascadeDelete.row.unpaidReduction')}
                value={<span dir="ltr">{formatPreviewAmount(row, lang)}</span>}
                testId={`delete-preview-unpaid-${row.currencyCode ?? row.currencyId}`}
              />
            ))}
            {preview.clawbackByCurrency.map((row) => (
              <Row
                key={`c-${row.currencyId}`}
                label={t('cascadeDelete.row.clawback')}
                value={<span dir="ltr">{formatPreviewAmount(row, lang)}</span>}
                testId={`delete-preview-clawback-${row.currencyCode ?? row.currencyId}`}
              />
            ))}
          </dl>
          {preview.clawbackByCurrency.length > 0 && (
            <p className="mt-2 text-xs text-amber-900 dark:text-amber-200">{t('cascadeDelete.clawbackNote')}</p>
          )}
          {clawbackOfficials.length > 0 && (
            <div className="mt-3 space-y-2" data-testid="delete-tournament-clawback-section">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-amber-900 dark:text-amber-200">
                {t('Officials who lose pay')}
              </h4>
              <ClawbackSummaryList officials={clawbackOfficials} testIdPrefix="delete-tournament-clawback" />
            </div>
          )}
        </section>
      )}

      {/* Everything finer: collapsed by default. Native <details> keeps keyboard and screen-reader
          semantics without a hidden input (no sr-only escape, see CLAUDE.md "Scrolling"). */}
      <details className="group rounded-lg border border-border" data-testid="delete-tournament-details">
        <summary className="flex cursor-pointer select-none items-center justify-between gap-2 px-4 py-3 text-sm font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg">
          {t('cascadeDelete.detailsToggle')}
          <Icon name="ChevronDown" size={16} className="shrink-0 transition-transform duration-200 ease-out group-open:rotate-180 motion-reduce:transition-none" />
        </summary>
        <div className="space-y-4 border-t border-border px-4 py-3">
          <dl className="divide-y divide-border">
            {preview.matchesByStatus.map((m) => {
              const s = resolveEnum(MatchStatus, m.status);
              return (
                <Row
                  key={String(m.status)}
                  label={t(MATCH_STATUS_KEYS[s ?? -1] ?? 'Unknown')}
                  value={m.count}
                  testId={`delete-preview-status-${s ?? 'x'}`}
                  muted
                />
              );
            })}
            <Row label={t('cascadeDelete.row.history')} value={preview.historyRowCount} testId="delete-preview-history" muted />
            <Row label={t('cascadeDelete.row.attachments')} value={preview.attachmentCount} testId="delete-preview-attachments" muted />
            <Row label={t('cascadeDelete.row.swapsCancelled')} value={preview.pendingSwapsToCancel} testId="delete-preview-swaps" muted />
            <Row label={t('cascadeDelete.row.legacyShifts')} value={preview.legacyMatchShiftsKept} testId="delete-preview-legacy-shifts" muted />
            <Row label={t('cascadeDelete.row.upcomingRemoved')} value={preview.upcomingMatchesRemoved} testId="delete-preview-upcoming" muted />
            <Row label={t('cascadeDelete.row.officialsNotified')} value={preview.officialsToNotify} testId="delete-preview-notified" muted />
            <Row label={t('cascadeDelete.row.neverPaid')} value={preview.assignmentsNeverPaid} testId="delete-preview-never-paid" muted />
          </dl>

          {preview.ratingChanges.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">{t('cascadeDelete.ratingsHeading')}</h4>
              <ul className="divide-y divide-border" data-testid="delete-preview-rating-changes">
                {preview.ratingChanges.map((r) => (
                  <li key={r.userId} className="flex items-center justify-between gap-3 py-1.5" data-testid={`delete-preview-rating-${r.userId}`}>
                    <span className="min-w-0 truncate text-sm text-foreground" title={r.fullName ?? undefined}>{r.fullName || t('common:UnknownUser')}</span>
                    <span className="flex shrink-0 items-center gap-2 text-sm tabular-nums">
                      <span className="text-muted-foreground">{r.before.toFixed(2)}</span>
                      <Icon name="ArrowRight" size={14} className="text-muted-foreground rtl:rotate-180" aria-hidden="true" />
                      <span className="font-semibold text-foreground">{r.after.toFixed(2)}</span>
                      {r.resetToZero && (
                        <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">{t('cascadeDelete.ratingReset')}</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </details>
    </div>
  );
};

export default DeleteTournamentPreview;
